import { Box, Skeleton, Stack } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import dt from '~/testing';

import type { SocureKycStatus, APIAuthUserData } from '~/services/users/types';
import { Avatar } from '~/domains/design-system/Avatar';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Tag } from '~/domains/design-system/Tag';
import IconShieldCheck from '~/domains/design-system/icons/IconShieldCheck';
import IconShieldX from '~/domains/design-system/icons/IconShieldX';

import useStyles from './UserDetail.styles';
import { TruncateText } from '~/domains/design-system/TruncateText';

function UserDetailLoading({ isCompact = false }: { isCompact?: boolean }) {
  const { classes } = useStyles({ isCompact });

  return (
    <div className={classes.userDetailContainer}>
      <Skeleton height={72} width={72} circle />
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}>
        <div className={classes.handleContainer}>
          <Skeleton height={30} width={140} />
        </div>
        <Skeleton height={20} width={200} />
      </Stack>
    </div>
  );
}

function UserDetail({
  user,
  kycStatus,
  isCompact,
}: {
  user?: APIAuthUserData;
  kycStatus?: SocureKycStatus;
  isCompact?: boolean;
}) {
  const { t } = useTranslation('account');
  const isVerified = kycStatus === 'accept';
  const { classes } = useStyles({ isCompact });

  if (!user) return <UserDetailLoading isCompact />;

  return (
    <div
      data-test-id={dt.myAccount.components.userDetail.container}
      className={classes.userDetailContainer}
    >
      <Avatar src={user.avatarUrl} size={isCompact ? 48 : 72} />
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={isCompact ? 0 : 8}>
        <div className={classes.handleContainer}>
          <Title variant={isCompact ? 'title-x-large' : 'headline-xxl'}>{user.handle}</Title>
          {!isCompact && isVerified && (
            <Tag variant="primary-stroke" icon={<IconShieldCheck size={16} />}>
              {t('myAccount.editAccount.verified.status')}
            </Tag>
          )}
          {!isCompact && !isVerified && (
            <Tag variant="secondary-stroke" icon={<IconShieldX size={16} />}>
              {t('myAccount.editAccount.notVerified.status')}
            </Tag>
          )}
        </div>
        {isCompact ? (
          <Box maw={220}>
            <TruncateText tooltipLabel={user.email} variant="title-small">
              {user.email}
            </TruncateText>
          </Box>
        ) : (
          <Text variant="title-medium">
            {user.firstName} {user.lastName} · {user.email}
          </Text>
        )}
      </Stack>
    </div>
  );
}

export default UserDetail;
